'use client';

import React, { useState } from 'react';
import { CheckSquare, Circle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface Task {
  id: string;
  title: string;
  contact: string;
  type: string;
  priority: 'high' | 'medium' | 'low';
  overdue: boolean;
  done: boolean;
}

const initialTasks: Task[] = [
  { id: 'task-t01', title: 'Send LinkedIn invite', contact: 'Maria Santos', type: 'LinkedIn', priority: 'high', overdue: false, done: false },
  { id: 'task-t02', title: 'Follow up after intro message', contact: 'David Okafor', type: 'Follow-up', priority: 'high', overdue: true, done: false },
  { id: 'task-t03', title: 'Share food safety whitepaper', contact: 'Priya Mehta', type: 'Content', priority: 'medium', overdue: false, done: false },
  { id: 'task-t04', title: 'Request meeting slot', contact: 'Tom Westerfield', type: 'Meeting', priority: 'high', overdue: true, done: false },
  { id: 'task-t05', title: 'Send connection message', contact: 'Elena Kovács', type: 'LinkedIn', priority: 'medium', overdue: false, done: false },
  { id: 'task-t06', title: 'Post-show follow-up email', contact: 'James Nakamura', type: 'Follow-up', priority: 'high', overdue: true, done: false },
];

const priorityColor: Record<string, string> = {
  high: 'text-red-500',
  medium: 'text-amber-500',
  low: 'text-slate-400',
};

export default function TodayTasksList() {
  const [tasks, setTasks] = useState(initialTasks);

  const toggle = (id: string) => {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id === id) {
          const next = { ...t, done: !t.done };
          if (next.done) toast.success(`Task completed`, { description: t.title });
          return next;
        }
        return t;
      })
    );
  };

  const pending = tasks.filter((t) => !t.done);
  const done = tasks.filter((t) => t.done);

  return (
    <div className="bg-white rounded-xl border border-border shadow-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CheckSquare size={15} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">Today's Tasks</h2>
        </div>
        <span className="text-[10px] font-mono font-semibold text-muted-foreground">
          {done.length}/{tasks.length} done
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-muted rounded-full h-1.5 mb-4 overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-500"
          style={{ width: `${(done.length / tasks.length) * 100}%` }}
        />
      </div>

      <div className="space-y-1.5">
        {pending.map((task) => (
          <div
            key={task.id}
            className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-muted/50 transition-colors group cursor-pointer"
            onClick={() => toggle(task.id)}
          >
            <button className="mt-0.5 shrink-0">
              <Circle size={15} className="text-muted-foreground group-hover:text-primary transition-colors" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {task.overdue && <AlertCircle size={11} className="text-red-500 shrink-0" />}
                <p className="text-xs font-medium text-foreground truncate">{task.title}</p>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-muted-foreground">{task.contact}</span>
                <span className="text-[10px] bg-muted text-muted-foreground px-1 py-0.5 rounded">{task.type}</span>
                {task.overdue && (
                  <span className="text-[10px] text-red-600 font-medium">Overdue</span>
                )}
              </div>
            </div>
            <AlertCircle size={12} className={`mt-0.5 shrink-0 ${priorityColor[task.priority]}`} />
          </div>
        ))}
        {done.map((task) => (
          <div
            key={task.id}
            className="flex items-start gap-2.5 p-2 rounded-lg opacity-50 cursor-pointer"
            onClick={() => toggle(task.id)}
          >
            <CheckSquare size={15} className="text-emerald-500 mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground line-through truncate">{task.title}</p>
          </div>
        ))}
      </div>
    </div>
  );
}